import Link from 'next/link';
import { memo, useMemo } from 'react';

import { type SavedFilter } from '@air/api/types';
import { PencilIcon } from '@air/design-icons';
import { ContextMenu, renderContextMenuItems } from '@air/primitive-context-menu';
import { IconButton } from '@air/primitive-icon-button';

import { usePrivateSearchSubSideNavSavedSearchItemMenuOptions } from '~/components/v2/components/workspace/search-nav/item/usePrivateSearchSubSideNavSavedSearchItemMenuOptions';
import { SubSideNavItemLabel } from '~/components/v2/components/workspace/sub-side-nav/item/SubSideNavItemLabel';
import { SubSideNavItemPrefix } from '~/components/v2/components/workspace/sub-side-nav/item/SubSideNavItemPrefix';
import { workspaceSidebarNavigationItem } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarNavigationItem';
import { getCanvasQueryParams } from '~/constants/canvasQueryParams';
import { createSavedSearchRoute } from '~/constants/routes';
import { useRouterSelector } from '~/hooks/useRouterSelector';
import { useSavedSearchIdSelector } from '~/hooks/useSavedSearchIdSelector';

export type SearchSubSideNavSavedSearchItemProps = {
  savedSearch: SavedFilter;
};

export const SearchSubSideNavSavedSearchItem = memo(({ savedSearch }: SearchSubSideNavSavedSearchItemProps) => {
  const savedSearchId = useSavedSearchIdSelector();
  const { canEdit, handleEditClick, menuOptions } = usePrivateSearchSubSideNavSavedSearchItemMenuOptions({
    savedSearch,
  });
  const href = createSavedSearchRoute(savedSearch.id);
  const isSelected = savedSearchId === savedSearch.id;
  const canvasQueryParams = useRouterSelector(() => getCanvasQueryParams()) ?? {};

  const item = useMemo(
    () => (
      <div
        className={workspaceSidebarNavigationItem({ appearance: 'subNav', className: 'h-8' })}
        data-selected={isSelected}
        data-title={savedSearch.name}
      >
        <Link
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded-8 text-inherit no-underline outline-none hover:no-underline ads:focus-visible:ring-primary focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset"
          data-item-id={savedSearch.id}
          data-selected={isSelected}
          data-testid="SUB_SIDE_NAV_ITEM"
          data-title={savedSearch.name}
          href={{ pathname: href, query: canvasQueryParams }}
        >
          <SubSideNavItemPrefix className="rounded-8 ads:bg-subtle bg-gray-3 text-12">
            {savedSearch.icon}
          </SubSideNavItemPrefix>
          <SubSideNavItemLabel>{savedSearch.name}</SubSideNavItemLabel>
        </Link>
        {canEdit && (
          <IconButton
            adsIcon
            appearance="ghost"
            className="hidden shrink-0 group-focus-within/workspace-sidebar-navigation-item:flex group-hover/workspace-sidebar-navigation-item:flex group-focus-visible/workspace-sidebar-navigation-item:flex"
            color="gray"
            data-id={savedSearch.id}
            data-name={savedSearch.name}
            data-testid="SIDEBAR_NAVIGATION_SAVED_SEARCHES_EDIT"
            label="Edit"
            onClick={handleEditClick}
            size="small"
            icon={PencilIcon}
          />
        )}
      </div>
    ),
    [canEdit, canvasQueryParams, handleEditClick, href, isSelected, savedSearch.icon, savedSearch.id, savedSearch.name],
  );

  if (menuOptions.length === 0) {
    return item;
  }

  return <ContextMenu trigger={item}>{renderContextMenuItems({ options: menuOptions })}</ContextMenu>;
});

SearchSubSideNavSavedSearchItem.displayName = 'SearchSubSideNavSavedSearchItem';
